import React from "react";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const hours = (seconds) => (seconds / 3600).toFixed(2);

export default function TipJobHours({ report }) {
  const [helpOpen, setHelpOpen] = React.useState(false);
  const unavailable = !report || report.status === "unavailable";
  const rows = unavailable ? [] : report.rows;
  const unresolved = rows.reduce((sum, row) => sum + (row.unresolved_seconds || 0), 0);
  const help = (
    <div>
      <p className="font-semibold">How hours are counted</p>
      <p>Total paid hours includes regular and overtime time, less unpaid breaks. In tip pools counts eligible, mapped jobs for linked employees during pool windows, once across overlapping pools. Training and excluded jobs remain in total hours. These hours do not change payouts.</p>
      {!unavailable && <p className="mt-2">{report.scope.join(" to ")} · {report.timezone} · Business day starts {Math.floor(report.day_start_hour).toString().padStart(2, "0")}:{Math.round((report.day_start_hour % 1) * 60).toString().padStart(2, "0")} local time. {report.status === "confirmed" ? "Saved at confirmation." : "Current synced punches; not a Toast comparison."} Total paid hours covers the full scope; pool hours use available saved runs. Totals are rounded after adding seconds.</p>}
    </div>
  );
  return (
    <>
      <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200" aria-label="Hours by job">
          <thead className="bg-slate-50"><tr>
            <th className="py-3 pl-5 pr-3 text-left text-xs font-bold uppercase text-slate-500">Job</th>
            <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Total paid hours</th>
            <th className="py-3 pl-3 pr-5 text-right text-xs font-bold uppercase text-slate-500">
              <span className="inline-flex items-center gap-1">In tip pools
                <Tooltip title={help} open={helpOpen} onOpen={() => setHelpOpen(true)} onClose={() => setHelpOpen(false)} describeChild disableTouchListener>
                  <button type="button" aria-label="How hours are counted" onClick={() => setHelpOpen(true)} onBlur={() => setHelpOpen(false)} onKeyDown={(event) => { if (event.key === "Escape") setHelpOpen(false); }} className="inline-flex min-h-[32px] min-w-[32px] items-center justify-center rounded text-slate-500 hover:text-violet-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600"><InfoOutlinedIcon style={{ fontSize: 16 }} /></button>
                </Tooltip>
              </span>
            </th>
          </tr></thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {unavailable ? <tr><td colSpan={3} className="px-5 py-8 text-center text-sm text-slate-500">{report?.reason || "Hours by job is unavailable for this range."}</td></tr> : rows.length === 0 ? <tr><td colSpan={3} className="px-5 py-8 text-center text-sm text-slate-500">No closed paid punches in this period.</td></tr> : rows.map((row) => (
              <tr key={row.key} className="hover:bg-slate-50">
                <td className="py-4 pl-5 pr-3 text-sm font-medium text-slate-900">{row.name}</td>
                <td className="px-3 py-4 text-right text-sm tabular-nums text-slate-900">{hours(row.paid_seconds)}</td>
                <td className="py-4 pl-3 pr-5 text-right text-sm tabular-nums text-slate-900">{hours(row.eligible_seconds)}</td>
              </tr>
            ))}
          </tbody>
          {!unavailable && rows.length > 0 && <tfoot className="bg-slate-50"><tr>
            <td className="py-3 pl-5 pr-3 text-sm font-semibold">Total</td>
            <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{hours(rows.reduce((sum, row) => sum + row.paid_seconds, 0))}</td>
            <td className="py-3 pl-3 pr-5 text-right text-sm font-semibold tabular-nums">{hours(rows.reduce((sum, row) => sum + row.eligible_seconds, 0))}</td>
          </tr></tfoot>}
        </table>
      </div>
      {!unavailable && (report.open_punches > 0 || unresolved > 0) && <p role="status" className="mt-2 text-xs text-amber-800">{[
        report.open_punches > 0 && `${report.open_punches} open punches are not included.`,
        unresolved > 0 && `${hours(unresolved)} paid hours have an unmapped job or unlinked employee; pool eligibility is unresolved.`,
      ].filter(Boolean).join(" ")}</p>}
    </>
  );
}

export function TipBreakdownTable({ report, children }) {
  const [view, setView] = React.useState("employee");
  return (
    <>
      <div role="group" aria-label="View tips by" className="mt-4 inline-flex rounded-lg border border-slate-200 bg-slate-100 p-1">
        {[['employee', 'By Employee'], ['job', 'By Job']].map(([value, label]) => <button key={value} type="button" aria-pressed={view === value} onClick={() => setView(value)} className={`min-h-[40px] rounded-md px-4 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 ${view === value ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>{label}</button>)}
      </div>
      {view === "job" ? <TipJobHours report={report} /> : children}
    </>
  );
}
